/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login, register, getGoogleLoginUrl, setUser } from "../reducers/login";
import { getAllKnowledgeBase, fetchActiveKnowledgeBase } from "../reducers/knowledgeBaseReducer";
import { fetchDocuments } from "../reducers/uploadDocReducer";
import { getAllChatSessions } from "../reducers/chatReducer";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import logo from "../assests/logo.png";
import GoogleButton from "react-google-button";
import videoFile from "../assests/video1.mp4"



const API_URL = process.env.REACT_APP_BASE_URL;


const Login = () => {

  const [isLogin, setIsLogin] = useState(true);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);


  // const userDetails = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { googleLoginUrl } = useSelector((state) => state.auth);

  const handleGoogleLogin = async () => {
    try {
      let redirectUrl = googleLoginUrl;

      if (!redirectUrl) {
        const result = await dispatch(getGoogleLoginUrl()).unwrap();
        redirectUrl = result?.google_login_url;
      }

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (_err) {
      Swal.fire("Error", "Google login is currently unavailable. Please try email login.", "error");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (isLogin) {
      if (!formData.email || !formData.password) {
        Swal.fire("Error", "Please enter email and password", "error");
        return;
      }

      const loginId = formData.email.trim();
      const isAdminLogin = loginId.toLowerCase() === "admin";
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginId);

      if (!isAdminLogin && !isValidEmail) {
        Swal.fire("Error", "Please enter a valid email", "error");
        return;
      }

      setIsSubmitting(true);
      dispatch(login({ username: loginId, password: formData.password }))
        .unwrap()
        .then((response) => {
          if (response.message === "Login successful" && response.user) {
            // Use user info directly from login response
            dispatch(setUser({
              username: response.user.username,
              email: response.user.email,
              subscription: response.user.roles,
            }));

            // Prefetch key data in background for faster page loads
            dispatch(getAllKnowledgeBase());
            dispatch(fetchActiveKnowledgeBase());
            dispatch(fetchDocuments());
            dispatch(getAllChatSessions());

            Swal.fire({
              title: "Success",
              text: "Login successful!",
              icon: "success",
              timer: 1500,
              showConfirmButton: false
            });

            navigate("/home");
          } else {
            setIsSubmitting(false);
            Swal.fire("Error", "Login failed!", "error");
          }
        })
        .catch((err) => {
          setIsSubmitting(false);
          const errorMessage = err?.detail || "Invalid credentials!";
          if (errorMessage === "Email is not verified") {
            Swal.fire("Verification Required", "Email is not verified. Please check your inbox.", "warning");
          } else {
            Swal.fire("Error", errorMessage, "error");
          }
        });
    } else {
      if (!formData.email || !formData.password || !formData.first_name || !formData.last_name || !formData.username) {
        Swal.fire("Error", "All fields are required for registration", "error");
        return;
      }

      setIsSubmitting(true);
      dispatch(register(formData))
        .unwrap()
        .then(() => {
          setIsSubmitting(false);
          Swal.fire("Success", "Registration successful! Please check your email to verify your account.", "success");
          setIsLogin(true);
        })
        .catch((err) => {
          setIsSubmitting(false);
          const error = err?.detail?.errorMessage || err?.detail || "Registration failed!";
          Swal.fire("Error", error, "error");
        });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      Swal.fire("Error", "Please enter your email", "error");
      return;
    }

    try {
      //BUG-
      // const res = await fetch(`${API_URL}/auth/forgot-password?email=${encodeURIComponent(forgotEmail)}`, {
      //   method: "POST",
      // });
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(forgotEmail)
      })

      if (res.ok) {
        Swal.fire("Success", "Password reset email sent!", "success");
        setShowForgot(false);
        setForgotEmail("");
      } else {
        const errorData = await res.json();
        Swal.fire("Error", errorData?.detail || "Something went wrong", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Server error. Try again later.", "error");
    }
  };

  return (
    <div className="flex h-screen w-full">
      {/* {loading && <Loader />}   */}
      <div className="relative w-1/2 h-full hidden lg:block">

        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover brightness-85 rounded-sm">
          <source src={videoFile} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center p-10 text-white">
          <h1 className="text-4xl font-bold">Welcome to Vectornest,</h1>
          <h2 className="text-xl font-semibold mt-2">Your Unified Retrieval-Augmented AI Workspace.</h2>
          <p className="mt-4 text-lg font-medium">
            Supercharge your workflow with intelligent chat, multi-source retrieval, document insights, and seamless knowledge integration — all in one place.
          </p>
        </div>




      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <img src={logo} alt="OmniNeura Logo" className="mx-auto h-12 w-auto" />
            <h2 className="mt-6 text-3xl font-bold text-gray-900">Welcome to Vectornest</h2>
          </div>

          <p className="text-sm text-gray-600 text-center">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <span className="text-blue-600 cursor-pointer" onClick={() => setIsLogin(!isLogin)}>
              {isLogin ? "Sign up" : "Log in"}
            </span>
          </p>

          <form onSubmit={handleFormSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <input type="text" name="username" placeholder="Username" onChange={handleChange} className="w-full p-3 border rounded-lg" />
                <input type="text" name="first_name" placeholder="First Name" onChange={handleChange} className="w-full p-3 border rounded-lg" />
                <input type="text" name="last_name" placeholder="Last Name" onChange={handleChange} className="w-full p-3 border rounded-lg" />
              </>
            )}

            <input
              type={isLogin ? "text" : "email"}
              name="email"
              placeholder="Email"
              onChange={handleChange}
              className="w-full p-3 border rounded-lg"
            />
            <input type="password" name="password" placeholder="Password" onChange={handleChange} className="w-full p-3 border rounded-lg" />

            {isLogin && (
              <div className="text-right">
                <span className="text-sm text-blue-600 cursor-pointer" onClick={() => setShowForgot(true)}>
                  Forgot password?
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-black text-white p-3 rounded-lg hover:bg-gray-800 transition ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {isSubmitting ? "Processing..." : isLogin ? "Log in" : "Sign up"}
            </button>
          </form>

          <div className="flex justify-center mt-10">
            <GoogleButton onClick={handleGoogleLogin} />
          </div>

          {showForgot && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-96 space-y-4">
                <h3 className="text-lg font-semibold">Forgot Password</h3>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full p-3 border rounded-lg"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowForgot(false)}
                    className="bg-gray-300 px-4 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleForgotPassword}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

};

export default Login;

